/** CAS 单点登录前端会话与路径约定（仅 enableCasLogin 店铺使用） */

import axios from "axios";
import cookie from "@/utils/cookies";

export const CAS_SERVICE_KEY = "cas_sso_service";
export const CAS_AUTO_REDIRECT_KEY = "cas_sso_auto_redirected";
export const CAS_GUEST_BROWSE_KEY = "cas_sso_guest_browse";
/** 即将进入 CAS 中转（用于首页确认后跳转，避免闪登录表单） */
export const CAS_TRANSIT_PENDING_KEY = "cas_sso_transit_pending";

/** 学校登记的 service 固定为 /login；实际跳转与验票在 /cas 完成，避免加载登录页 */
export const CAS_CALLBACK_PATH = "/login";
export const CAS_TRANSIT_PATH = "/cas";

export const DEFAULT_CAS_SERVER_URL = "https://ids.xidian.edu.cn/authserver";

/** APP / 弱网下验票或取 IDS 地址的超时（毫秒），避免一直停在「正在登录」 */
export const CAS_REQUEST_TIMEOUT_MS = 10000;
/** 不强制静默跳转的页面 */
export const CAS_GUARD_SKIP_PATHS = [
  "/login",
  "/cas",
  "/register",
  "/resetpwd",
  "/settlement",
  "/contract",
  "/about",
];

export function casSessionGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

export function casSessionSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
}

export function casSessionRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {}
}

export function isCasGuestBrowse() {
  return casSessionGet(CAS_GUEST_BROWSE_KEY) === "1";
}

export function allowCasGuestBrowse() {
  casSessionSet(CAS_GUEST_BROWSE_KEY, "1");
}

export function clearCasGuestBrowse() {
  casSessionRemove(CAS_GUEST_BROWSE_KEY);
}

export function clearCasLoginSessionFlags() {
  casSessionRemove(CAS_AUTO_REDIRECT_KEY);
  casSessionRemove(CAS_SERVICE_KEY);
  casSessionRemove(CAS_TRANSIT_PENDING_KEY);
}

export function markCasTransitPending() {
  casSessionSet(CAS_TRANSIT_PENDING_KEY, "1");
}

export function clearCasTransitPending() {
  casSessionRemove(CAS_TRANSIT_PENDING_KEY);
}

export function isCasTransitPending() {
  return casSessionGet(CAS_TRANSIT_PENDING_KEY) === "1";
}

export function shouldSkipCasGuardPath(path) {
  if (!path) {
    return true;
  }
  return CAS_GUARD_SKIP_PATHS.some(
    (p) => path === p || path.startsWith(p + "/")
  );
}

export function isCasLoginEnabled(config) {
  return config?.enableCasLogin === true || config?.enableCasLogin === 1;
}

/**
 * 给 Promise 加超时。超时后调用方停止等待；原请求仍可能继续，但其结果会被忽略且不会产生未处理 rejection。
 */
export function withTimeout(
  promise,
  ms = CAS_REQUEST_TIMEOUT_MS,
  message = "请求超时，请重试"
) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(message));
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * 客户端补种 shopId：APP 冷启动 / 部分 WebView 可能丢 cookie，
 * 而 req() 无 sid 会直接「店铺不存在」，导致验票永远卡在「正在登录」。
 * 本接口按域名解析店铺，不依赖已有 sid。
 */
export async function ensureClientShopId() {
  if (typeof window === "undefined") {
    return "";
  }
  const existing = cookie.getInClient("shopId");
  if (existing) {
    return String(existing);
  }
  // host 含端口；hostname 不含。库里多为纯主机名，两种都试，避免解析失败误伤验票。
  const candidates = [
    window.location.host,
    window.location.hostname,
  ].filter((v, i, arr) => v && arr.indexOf(v) === i);

  for (const domain of candidates) {
    try {
      const { data } = await axios.get("/gateway/base/api/shop/website/domain", {
        params: { domain },
        timeout: 8000,
      });
      const shop = data?.data;
      if (data?.code === 200 && shop?.id) {
        const id = String(shop.id);
        cookie.setInClient({ key: "shopId", val: id });
        if (shop.shopIcon) {
          cookie.setInClient({ key: "shopIcon", val: shop.shopIcon });
        }
        if (shop.shopName) {
          cookie.setInClient({ key: "shopName", val: shop.shopName });
        }
        return id;
      }
    } catch (e) {
      console.error("ensureClientShopId failed", domain, e);
    }
  }
  return "";
}

/** 是否手机 UA（H5 CAS 验票 / 交接用；与 server middleware 判断对齐） */
export function isMobileCasUa(ua) {
  const raw =
    ua ||
    (typeof navigator !== "undefined" ? navigator.userAgent : "") ||
    "";
  return /Android|iPhone|iPad|iPod/i.test(raw);
}

/** 手机验票成功后的 H5 落地地址 */
export function resolveH5LandingUrl(redirectQuery) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  let path = "/h5/";
  if (redirectQuery) {
    let raw = String(redirectQuery);
    try {
      raw = decodeURIComponent(raw);
    } catch (e) {}
    if (raw.startsWith("/h5")) {
      path = raw;
    } else if (raw.startsWith("/") && raw !== "/login" && raw !== "/cas") {
      // 业务路径尽量映射到 H5 首页；精确映射交给既有 middleware，此处保底进 H5
      path = "/h5/";
    }
  }
  return `${origin}${path}`;
}

/**
 * 读取有效的 H5 学员 token。
 * uni-app 可能把 null 写成 {"type":"object","data":null}，不能当已登录。
 */
export function getH5StudentToken() {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    const raw = localStorage.getItem("EDU_H5_TOKEN");
    if (!raw || raw === "null" || raw === "undefined") {
      return "";
    }
    try {
      const obj = JSON.parse(raw);
      if (obj && typeof obj === "object" && "data" in obj) {
        const data = obj.data;
        return typeof data === "string" && data && data !== "null"
          ? data
          : "";
      }
    } catch (e) {
      // 纯字符串 token（含 JWT）
    }
    return raw;
  } catch (e) {
    return "";
  }
}

/** 写入 H5 可读的学员 token（与 uni.setStorageSync 字符串格式一致） */
export function setH5StudentToken(token) {
  if (!token || typeof window === "undefined") {
    return;
  }
  try {
    // uni-app H5：string 类型直接存原文
    localStorage.setItem("EDU_H5_TOKEN", String(token));
  } catch (e) {}
}

export function clearH5StudentToken() {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.removeItem("EDU_H5_TOKEN");
  } catch (e) {}
}

export function normalizeCasServerUrl(casServerUrl) {
  let url = (casServerUrl || DEFAULT_CAS_SERVER_URL).trim();
  while (url.endsWith("/")) {
    url = url.slice(0, -1);
  }
  return url || DEFAULT_CAS_SERVER_URL;
}

/** 与学校登记一致的 service（始终 /login，即使当前在 /cas） */
export function buildCasLoginService(origin) {
  const base =
    origin ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${CAS_CALLBACK_PATH}`;
}

/** 管理端入口域名解析（西电等同域 /admin） */
export function resolveAdminBaseUrl() {
  if (typeof window === "undefined") {
    return "";
  }
  const host = window.location.hostname;
  if (host === "192.168.10.61") {
    return "http://192.168.10.61:9527";
  }
  if (host === "edu.shanxiyixue.com") {
    return "https://edu.shanxiyixue.com";
  }
  if (host === "edu-test.ykumda.com") {
    return "https://edu-test.ykumda.com";
  }
  return window.location.origin;
}

export function normalizeCasService(raw) {
  try {
    const u = new URL(String(raw || ""), "http://local.invalid");
    return `${u.origin}${u.pathname}`.replace(/\/$/, "") || String(raw || "");
  } catch (e) {
    return String(raw || "").split("?")[0].split("#")[0];
  }
}

export function rememberCasService(service) {
  casSessionSet(CAS_SERVICE_KEY, normalizeCasService(service));
}

export function restoreCasService() {
  const cached = casSessionGet(CAS_SERVICE_KEY);
  if (cached) {
    return normalizeCasService(cached);
  }
  return buildCasLoginService();
}

export function clearCasServiceCache() {
  casSessionRemove(CAS_SERVICE_KEY);
}

export function markCasAutoRedirected() {
  casSessionSet(CAS_AUTO_REDIRECT_KEY, "1");
}

export function clearCasAutoRedirected() {
  casSessionRemove(CAS_AUTO_REDIRECT_KEY);
}

export function hasCasAutoRedirected() {
  return casSessionGet(CAS_AUTO_REDIRECT_KEY) === "1";
}

/**
 * /login 是否应在渲染登录页组件之前改走 /cas（避免加载登录表单）。
 * 仅依据店铺 enableCasLogin / 已确认的 CAS 中转意图，禁止按主机名强制。
 * ticket 回调：先改走 /cas，由 /cas 页校验店铺开关后再验票。
 */
export function shouldBypassLoginPageForCas(to, host) {
  if (!to || to.path !== CAS_CALLBACK_PATH) {
    return false;
  }
  const local = to.query?.localLogin;
  if (local === "1" || local === "true") {
    return false;
  }
  // IDS 回跳带 ticket：交由 /cas 校验 enableCasLogin 后处理
  if (to.query?.ticket) {
    return true;
  }
  // 用户已在 CAS 店点击登录并标记中转
  if (process.client && isCasTransitPending()) {
    return true;
  }
  // 仅当缓存中明确为本店开启 CAS 时提前改走（避免首屏闪登录表）
  if (process.client) {
    try {
      const raw = sessionStorage.getItem("shopInfo");
      if (raw) {
        const shop = JSON.parse(raw);
        if (isCasLoginEnabled(shop?.config)) {
          return true;
        }
      }
    } catch (e) {}
  }
  return false;
}

/**
 * 退出本平台后，同步退出学校 IDS，并回跳到带 guest 的首页，
 * 避免 IDS 会话仍在导致立刻免登回来。
 */
export function buildCasLogoutUrl(casServerUrl, returnUrl) {
  const server = normalizeCasServerUrl(casServerUrl);
  const target =
    returnUrl ||
    (typeof window !== "undefined"
      ? `${window.location.origin}/?guest=1`
      : "/?guest=1");
  return `${server}/logout?service=${encodeURIComponent(target)}`;
}

/** 清除本平台登录态后的跳转（CAS 店走 IDS logout） */
export function redirectAfterLocalLogout(shopConfig) {
  clearCasLoginSessionFlags();
  if (isCasLoginEnabled(shopConfig)) {
    allowCasGuestBrowse();
    window.location.href = buildCasLogoutUrl(shopConfig?.casServerUrl);
    return;
  }
  window.location.href = "/";
}
